/**
 * Sharing the result card. A web page can't post to Instagram: the most it can
 * do is hand an image to the phone's share sheet. The clickable link has to be
 * added by the person, with Instagram's Link sticker, from a link we copy for
 * them. Nothing here can tell whether anything was actually posted.
 */

/** Where the Link sticker points. The utm tags land in the results `utm` column. */
export const SHARE_URL =
  "https://ai-readiness.mileseducation.com/?utm_source=instagram&utm_medium=organic_social&utm_campaign=result_share";

/** Printed on the card. Text in an image is never clickable; it is the fallback. */
export const SHARE_HOST = "ai-readiness.mileseducation.com";

export type ShareOutcome = "shared" | "cancelled" | "fallback";

/** Whether this browser can hand this file to the share sheet. Not Instagram detection. */
export function canShareFile(file: File): boolean {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    );
  } catch {
    return false;
  }
}

/**
 * Opens the share sheet with the image alone. Call it straight from the tap
 * with an image that is already drawn: iOS drops the share if slow work sits
 * between the tap and this call. "shared" only means the sheet completed.
 */
export async function shareImage(file: File): Promise<ShareOutcome> {
  if (!canShareFile(file)) return "fallback";
  try {
    await navigator.share({ files: [file] });
    return "shared";
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") return "cancelled";
    return "fallback";
  }
}

/** Copies the link. False when the clipboard is unavailable or refused. */
export async function copyLink(url: string = SHARE_URL): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/** Saves the image. In-app browsers often ignore this; the preview's long-press is the backstop. */
export function downloadImage(file: File) {
  const href = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = href;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
